<template>
  <div>
    <portal to="global-modal-portal" v-if="isPaymentRequiredForReportActive"  :disabled="!useModalPortal" id="payment-modal">
      <b-modal :active.sync="isPaymentRequiredForReportActive" :width="480" scroll="keep" :has-modal-card="true" :can-cancel="false">
        <div class="modal-card">
          <header class="modal-card-head" style="width: 480px !important">
            <p class="modal-card-title">Payment required!</p>
          </header>
          <section class="modal-card-body" style="width: 480px !important; padding: 10px !important;">
            <div class="notification is-flex is-link">
              <span><i class="fas fa-exclamation-circle is-warning fa-2x"></i></span>
                <span class="ml-10">
                  {{ paymentRequireModalMessage }}
                </span>
            </div>
          </section>
          <footer class="modal-card-foot" style="width: 480px !important">
            <button class="button is-primary" @click="purchaseCarfaxReport()">Yes</button>
            <button class="button is-danger" @click="closePaymentRequiredModal">Cancel</button>
          </footer>
        </div>
      </b-modal>
    </portal>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex'
import appraisalService from '@/services/appraisalService'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import eventBus from '@/eventBus'

const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapUserGetters = createNamespacedHelpers('user').mapGetters

export default {
  name: 'CarFaxPaymentRequiredControl',
  components: {
    'error-display-component': ErrorDisplayComponent
  },
  props: {
    vinNo: {
      type: String,
      default: ''
    },
    inventory: {
      type: Object,
      default: null
    },
    useModalPortal:  {
      type: Boolean,
      default: true
    },
    showPaymentRequiredModal:  {
      type: Boolean,
      default: false
    },
    //⚠ The purchase goes through the combined endpoint, which extracts the CARFAX history as well as the
    //rating. The RO screens never use the history, so they turn it off rather than pay for an OpenAI
    //extraction and wait for it.
    includeHistory: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isRefreshing: false,
      isPaymentRequiredForReportActive: false,
      isSaving: false,
      serverErrorMessage: '',
      serverErrors: []
    }
  },
  computed: {
    ...mapUserGetters(['currentUser', 'hasFeatureAccess']),
    ...mapDealerState(['currentDealer']),
    paymentRequireModalMessage: function () {
      if (this.vinNo && this.vinNo.length > 0) {
        return 'The CARFAX report for vehicle with vin number ' + this.vinNo + ' is not among your existing reports and will need to be purchased. Would you like to purchase the report?'
      }
      return 'The CARFAX report is not among your existing reports and will need to be purchased. Would you like to purchase the report?'
    },
    currentUserHasCarfaxSession: function () {
      if (this.currentUser && this.currentUser.carFaxSession && this.currentUser.carFaxSession.hasValidSession) {
        return true
      }
      return false
    }
  },
  methods: {
    purchaseCarfaxReport: function () {
      this.getVehicleCarFaxRatingPurchaseIfRequired()
      this.isPaymentRequiredForReportActive = false
    },
    getVehicleCarFaxRatingPurchaseIfRequired: function () {
      let userId = this.currentUser.id
      let that = this
      let vinNo = this.vinNo
      let inventoryId = this.inventory ? this.inventory.id : 0
      if (this.currentUserHasCarfaxSession && this.isRefreshing === false && vinNo !== '') {
        that.$emit('on-carfax-purchase-requested')
        that.isRefreshing = true
        appraisalService.extractCarfaxRatingFromDealerReport(userId, vinNo, true, inventoryId, that.includeHistory).then(response => {
          that.isRefreshing = false
          if (response.errors) {
            that.serverErrors = response.errors
            that.currentCarFaxRating = null
            that.$emit('on-carfax-purchase-failed')
          } else {
            if (response) {
              that.currentCarFaxRating = response.carfaxRating
            } else {
              that.currentCarFaxRating = null
            }
            //⚠ The history comes back on this same response. It is handed on as a second argument - which
            //listeners taking one parameter simply ignore - so they apply it rather than fetch it again.
            that.$emit('on-new-carfax-purchased', that.currentCarFaxRating, response && response.detailedHistory)
          }
        }).catch((error) => {
          that.isRefreshing = false
          that.$emit('on-carfax-purchase-failed')
          that.serverErrorMessage = error.message
        })
      } else {
        if (that.currentUserHasCarfaxSession === false) {
          that.$emit('on-carfax-login-required', that.currentCarFaxRating)
        }
        that.$emit('on-carfax-purchase-failed')
        that.isRefreshing = false
      }
    },
    closePaymentRequiredModal: function () {
      this.isPaymentRequiredForReportActive = false
      this.$emit('carfax-payment-modal-closed')
    }
  },
  watch: {
    showPaymentRequiredModal: function (newValue, oldValue) {
      this.isPaymentRequiredForReportActive = newValue
    }
  }
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

  .status-icon {
    width: 13px;
    height: 12px;
    margin-right:12px;
  }

  .carfax-container-display-only {
    display: inline-flex;
    flex-flow: row;
    align-items: center;
    margin-top:10px;
  }

  .carfax-container {
    margin-top: 20px;
    padding: 0;
    margin-left:0;
    margin-right:0;
  }

  .carfax-owner-icons {
    height: 12px;
    width: 12px;
    margin-top:-15px;
  }

  .carfax-warning {
    background-image: url('@/assets/static/carfax/ico_accident.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .carfax-danger {
    background-image: url('@/assets/static/carfax/ico_alert.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .carfax-recall {
    background-image: url('@/assets/static/carfax/ico_recall.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .carfax-ok {
    background-image: url('@/assets/static/carfax/ico_green_check.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .refresh-status-label {
    margin-left:1em;
  }

  .cd .column {
    padding:0;
  }
  
  #payment-modal .modal {
    z-index: 10000 !important;
  }
</style>
